import { api } from "../../api"
import { hasPendingMutations } from "../../utils/rtk-query-util"
import { makeUrlSearchParams } from "../../utils/url"
import { makeLinkApiEndpoints } from "./endpoints"
import { LinkViewModel, makeLinkViewModel } from "./link"
import { LinkResponse, UrlSchema } from "./link-response"

const apiEndpoints = makeLinkApiEndpoints()

export const linkApi = api.injectEndpoints({
  endpoints: (builder) => {
    return {
      getLinks: builder.query<LinkViewModel[], GetLinksQuery>({
        query: (params) => {
          return {
            method: "GET",
            url: apiEndpoints.links(),
            params: params ? makeUrlSearchParams(params) : undefined,
          }
        },
        transformResponse: (response: LinkResponse[]) => response.map(makeLinkViewModel),
        providesTags: ["Link"],
      }),
      createLink: builder.mutation<void, NewLinkData>({
        query: (linkData) => {
          return {
            method: "POST",
            url: apiEndpoints.links(),
            body: linkData,
          }
        },
        onQueryStarted: async (linkData, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            linkApi.util.updateQueryData(
              "getLinks",
              { entityId: linkData.entityId, entityType: linkData.entityType },
              (draft) => {
                const parseResult = UrlSchema.safeParse(linkData.url)
                if (parseResult.success) {
                  draft.push(
                    makeLinkViewModel({
                      id: "", // Temporary ID to make typescript happy
                      ...linkData,
                    })
                  )
                }
              }
            )
          )
          try {
            await queryFulfilled
          } catch {
            patchResult.undo()
          }
        },
        invalidatesTags: ["Link", "Task", "Project"],
      }),
      updateLink: builder.mutation<void, { linkId: string } & LinkUpdateData & GetLinksQuery>({
        query: ({ linkId, ...updateData }) => {
          return {
            method: "PUT",
            url: apiEndpoints.linkItem(linkId),
            body: updateData,
          }
        },
        async onQueryStarted({ linkId, entityId, entityType, ...updateData }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            linkApi.util.updateQueryData("getLinks", { entityId, entityType }, (draft) => {
              const link = draft.find((l) => l.id === linkId)
              if (link) {
                if (updateData.url !== undefined) {
                  const parseResult = UrlSchema.safeParse(updateData.url)
                  if (!parseResult.success) {
                    return
                  }
                }
                Object.assign(link, updateData)
              }
            })
          )
          try {
            await queryFulfilled
          } catch {
            patchResult.undo()
          }
        },
        invalidatesTags: ["Link", "Task", "Project"],
      }),
      deleteLink: builder.mutation<void, { linkId: string } & GetLinksQuery>({
        query: (params) => {
          return {
            method: "DELETE",
            url: apiEndpoints.linkItem(params.linkId),
          }
        },
        onQueryStarted: async ({ linkId, entityId, entityType }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            linkApi.util.updateQueryData("getLinks", { entityId, entityType }, (draft: LinkViewModel[]) => {
              const index = draft.findIndex((link) => link.id === linkId)
              if (index !== -1) {
                draft.splice(index, 1)
              }
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: "deleteLink" }))
              dispatch(linkApi.util.invalidateTags(["Link", "Task", "Project"]))
          } catch {
            patchResult.undo()
          }
        },
      }),
    }
  },
})

export const { useGetLinksQuery, useCreateLinkMutation, useUpdateLinkMutation, useDeleteLinkMutation } = linkApi

export type GetLinksQuery = {
  entityId: string
  entityType: "task" | "project"
}

export type NewLinkData = {
  entityId: string
  entityType: "task" | "project"
  url: string
  title: string
}

export type LinkUpdateData = {
  title?: string
  url?: string
}
